import { alpha, createTheme } from "@mui/material/styles";

// The app's one MUI theme, and the single place raw color values live (see DESIGN_SYSTEM.md).
// src/index.js builds it for the user's light/dark mode; a page that is always light (the public
// landing-style pages) builds it with "light" through <LightThemeScope> instead of writing its own.
const SF_PRO =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif';

export function buildAppTheme(mode) {
  return createTheme({
    palette: {
      mode,
      // ── Brand colours ──────────────────────────────────────────────
      primary:   { main: "#393E46" }, // Red  — Add / Save / Create / Delete-confirm
      secondary: { main: "#e01219" }, // Blue — Edit / Export / Assign / Submit
      tertiary:  { main: "#4a4c4f" }, // Slate — toggles / active states
      // ── Backgrounds ────────────────────────────────────────────────
      background: {
        default: mode === "light" ? "#f4f6f8" : "#121212",
        paper:   mode === "light" ? "#ffffff"  : "#1e1e1e",
        drawer:  mode === "light" ? "#ffffff"  : "#1e1e1e",
        profileBackground: mode === "light" ? "#F4F2EE" : "#121212",
      },
      faintText: {
        main:         mode === "light" ? "#7d7d82" : "#a5a5ad",
        contrastText: mode === "light" ? "#111111" : "#ffffff",
      },
      // Active nav-item background in drawer
      drawer: {
        main:         mode === "light" ? "#393E46" : "#393E46",
        contrastText: "#ffffff",
      },
      action: {
        hover: mode === "light"
          ? "rgba(0, 0, 0, 0.04)"
          : "rgba(255, 255, 255, 0.08)",
      },
      divider: mode === "light" ? "#e5e7eb" : "rgba(255,255,255,0.08)",
      text: {
        primary:   mode === "light" ? "#111827" : "#f2f2f2",
        secondary: mode === "light" ? "#6b7280" : "#c7c7cc",
      },
      // ── Extended semantic tokens ─────────────────────────────────────
      // No native MUI equivalent (row backgrounds, before/after diff panels,
      // approval-status pills, named feature accents, toggle-pill classes).
      // This is the single source of truth for them — components read them
      // through the `useAppColors()` hook (src/theme/useAppColors.js) rather
      // than duplicating raw hex values locally.
      // Text boxes (every outlined MUI input: TextField, Select, DatePicker). Applied globally by
      // the MuiOutlinedInput override below, so a text box is visible on any card in either mode:
      // a fill one step off the card, and an outline strong enough to read as a box.
      input: {
        bg:          mode === "light" ? "#ffffff" : "#2a2a2a",
        border:      mode === "light" ? "#9ca3af" : "rgba(255,255,255,0.32)",
        borderHover: mode === "light" ? "#6b7280" : "rgba(255,255,255,0.32)",
        borderFocus: mode === "light" ? "#393E46" : "rgba(255,255,255,0.32)",
        placeholder: mode === "light" ? "#6b7280" : "#a1a1aa",
        disabledBg:  mode === "light" ? "#f3f4f6" : "rgba(255,255,255,0.04)",
      },
      cardRow:   mode === "light" ? "#f9fafb" : "#252525",
      sectionBg: mode === "light" ? "#f9fafb" : "#252525",
      diff: {
        from: {
          bg:     mode === "light" ? "#fff5f5" : "rgba(239,68,68,0.07)",
          border: mode === "light" ? "#fecaca" : "rgba(239,68,68,0.25)",
          text:   "#ef4444",
        },
        to: {
          bg:     mode === "light" ? "#f0fdf4" : "rgba(34,197,94,0.07)",
          border: mode === "light" ? "#bbf7d0" : "rgba(34,197,94,0.25)",
          text:   "#22c55e",
        },
      },
      approvalStatus: {
        approved: { bg: "rgba(34,197,94,0.15)",  text: "#22c55e" },
        rejected: { bg: "rgba(239,68,68,0.15)",  text: "#ef4444" },
        pending:  { bg: "rgba(234,179,8,0.15)",  text: "#ca8a04" },
      },
      pillActive: mode === "dark"
        ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/30"
        : "bg-indigo-50 text-indigo-700 border border-indigo-200",
      pillInactive: mode === "dark"
        ? "border border-white/10 text-[#c7c7cc] hover:border-white/20 hover:text-[#e8e8e8]"
        : "border border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700",
      accents: {
        indigo: {
          main: "#6366f1",
          bg:     mode === "light" ? "#eef2ff" : "rgba(99,102,241,0.14)",
          border: mode === "light" ? "#c7d2fe" : "rgba(99,102,241,0.35)",
          text:   mode === "light" ? "#4338ca" : "#a5b4fc",
        },
        amber: {
          main: "#f59e0b",
          bg:     mode === "light" ? "#fffbeb" : "rgba(245,158,11,0.14)",
          border: mode === "light" ? "#fde68a" : "rgba(245,158,11,0.35)",
          text:   mode === "light" ? "#b45309" : "#fcd34d",
        },
        teal: {
          main: "#14b8a6",
          bg:     mode === "light" ? "#f0fdfa" : "rgba(20,184,166,0.14)",
          border: mode === "light" ? "#99f6e4" : "rgba(20,184,166,0.35)",
          text:   mode === "light" ? "#0f766e" : "#5eead4",
        },
        rose: {
          main: "#e11d48",
          bg:     mode === "light" ? "#fff1f2" : "rgba(225,29,72,0.14)",
          border: mode === "light" ? "#fecdd3" : "rgba(225,29,72,0.35)",
          text:   mode === "light" ? "#be123c" : "#fda4af",
        },
        emerald: {
          main: "#10b981",
          bg:     mode === "light" ? "#ecfdf5" : "rgba(16,185,129,0.14)",
          border: mode === "light" ? "#a7f3d0" : "rgba(16,185,129,0.35)",
          text:   mode === "light" ? "#047857" : "#6ee7b7",
        },
        violet: {
          main: "#8b5cf6",
          bg:     mode === "light" ? "#f5f3ff" : "rgba(139,92,246,0.14)",
          border: mode === "light" ? "#ddd6fe" : "rgba(139,92,246,0.35)",
          text:   mode === "light" ? "#6d28d9" : "#c4b5fd",
        },
      },
    },
    typography: {
      fontFamily: SF_PRO,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          body {
            font-family: ${SF_PRO};
          }
        `,
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: "6px", textTransform: "none" },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: "6px" },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-selected": {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.getContrastText(theme.palette.primary.main),
            },
            "&.Mui-selected .MuiListItemIcon-root": { color: "inherit" },
            "&:hover.Mui-selected": {
              backgroundColor: alpha(theme.palette.primary.main, 0.85),
            },
          }),
        },
      },
      // Every outlined text box takes its fill, outline and placeholder from palette.input. In
      // dark mode hover/focus only thicken the outline (MUI's default width change) and keep the
      // same color; in light mode they darken it.
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => {
            const input = theme.palette.input;
            return {
              backgroundColor: input.bg,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: input.border },
              "&:hover:not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": {
                borderColor: input.borderHover,
              },
              "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
                borderColor: input.borderFocus,
              },
              "&.Mui-disabled": { backgroundColor: input.disabledBg },
              "& input::placeholder, & textarea::placeholder": { color: input.placeholder, opacity: 1 },
            };
          },
        },
      },
      // Same rule for the floating label: keep its default color on focus in dark mode.
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) =>
            theme.palette.mode === "dark"
              ? {
                  "&.Mui-focused:not(.Mui-error)": {
                    color: theme.palette.text.secondary,
                  },
                }
              : {},
        },
      },
    },
  });
}
